import { api } from "api/base";
import type { EncodingProfilePreset, Stream } from "./Stream.types";

export const STREAMS_ENDPOINT = `/v1/streams`;

/**
 * @summary - Fetches all available streams available to the given user
 * @param userId
 * @return A list of stream source objects
 */
export const getStreams = async (
    userId: string
): Promise<{ data: Stream[] }> => {
    const { data } = await api.get(STREAMS_ENDPOINT, {
        params: { userId }
    });
    return data;
};

/**
 * @summary - Fetches the source of a specific stream
 * @async
 * @param streamId
 * @return A stream source object
 */
export const getStream = async (streamId: string): Promise<Stream> => {
    const { data } = await api.get(`${STREAMS_ENDPOINT}/${streamId}`);
    return data;
};

/**
 * @summary - Updates the source of a specified stream
 * @param id
 * @param encodeSource
 * @return A stream source object
 */
export const updateEncodeSource = async (
    id: string,
    encodeSource: string,
    skipEncodeUrlSourceHealthcheck = false
): Promise<Stream> => {
    try {
        const { data } = await api.put(
            `${STREAMS_ENDPOINT}/${id}/encode/source`,
            {
                encodeSource,
                skipEncodeUrlSourceHealthcheck
            }
        );

        return data;
    } catch (error) {
        // This is the deeply nested error message specific to encodeSource
        const message = error?.response?.data?.message;
        if (message) {
            throw new Error(message);
        }

        throw error;
    }
};

/**
 * @summary - Turns on a specified stream
 * @param streamId
 * @return A stream object
 */
export const turnOnStream = async (
    streamId: string,
    requestData = {}
): Promise<Stream> => {
    const {
        data: { data }
    } = await api.patchJSON(
        `${STREAMS_ENDPOINT}/${streamId}/status/online`,
        requestData
    );
    return data;
};

/**
 * @summary - Turns off a specified stream
 * @param streamId
 * @return A stream object
 */
export const turnOffStream = async (
    streamId: string,
    requestData = {}
): Promise<Stream> => {
    const {
        data: { data }
    } = await api.patchJSON(
        `${STREAMS_ENDPOINT}/${streamId}/status/offline`,
        requestData
    );
    return data;
};

/**
 * @summary - Gets the preset for an encoding profile
 * @param econdingProfileId
 * @return A stream object
 */
export const getEncodingProfilePreset = async (
    encodingProfileId: string
): Promise<EncodingProfilePreset> => {
    const {
        data: { data }
    } = await api.get(`v1/encodes/profiles/${encodingProfileId}/preset`);
    return data;
};

/**
 * @summary - Set a schedule for a stream
 * @param streamId
 * @param requestData The schedule data to set
 */
export const setStreamSchedule = async (streamId: string, requestData = {}) => {
    await api.postJSON(`${STREAMS_ENDPOINT}/${streamId}/schedule`, requestData);
};

/**
 * @summary - Remove a schedule for a stream
 * @param streamId
 */
export const deleteStreamSchedule = async (streamId: string) => {
    await api.remove(`${STREAMS_ENDPOINT}/${streamId}/schedule`);
};

/**
 * @summary - Get the schedule info for a stream
 * @param streamId
 * @returns The schedule info for the stream
 */
export const getStreamScheduleInfo = async (streamId: string) => {
    //TODO: unused?
    const { data } = await api.get(
        `${STREAMS_ENDPOINT}/${streamId}/schedule/info`
    );

    return data;
};

/**
 * @summary - Update fields of a specific stream
 * @param streamId
 * @param requestData the fields to update within the stream
 * @returns The model of the stream that was updated
 */
export const updateStream = async (
    streamId: string,
    requestData: {
        coverImage?: string;
        displayName?: string;
        epgEnabled?: boolean; //TODO: which one of these is right
        users?: string; //array of comma separated ids
    }
) => {
    const { data } = await api.putJSON(
        `${STREAMS_ENDPOINT}/${streamId}`,
        requestData
    );
    return data;
};
